import { useState } from "react";
import { createPortal } from "react-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  FolderOpen,
  CheckSquare,
  Users,
  PhilippinePeso,
  X,
} from "lucide-react";
import { Project, Task, User as UserType } from "../../types";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { AnnouncementBoard } from "./AnnouncementBoard";
import { navigateToView } from "../../utils/appRoutes";

interface DashboardStatsProps {
  projects: Project[];
  tasks: Task[];
  users: UserType[];
  currentUser: UserType;
}

const COLORS = [
  "#2563eb",
  "#059669",
  "#d97706",
  "#ea580c",
  "#475569",
  "#0284c7",
];
const peso = "\u20B1";

const formatCompactAmount = (value: number) => {
  if (!Number.isFinite(value)) return "0";
  const absValue = Math.abs(value);
  const formatScaled = (denominator: number, suffix: string) => {
    const scaled = Math.trunc((value / denominator) * 10) / 10;
    const formatted = scaled.toFixed(1).replace(/\.0$/, "");
    return `${formatted} ${suffix}`;
  };
  if (absValue >= 1_000_000_000_000)
    return formatScaled(1_000_000_000_000, "T");
  if (absValue >= 1_000_000_000) return formatScaled(1_000_000_000, "B");
  if (absValue >= 1_000_000) return formatScaled(1_000_000, "M");
  return Math.trunc(value).toLocaleString();
};

export function DashboardStats({
  projects,
  tasks,
  users,
  currentUser,
}: DashboardStatsProps) {
  const [showRevenueDetails, setShowRevenueDetails] = useState(false);

  const getFilteredData = () => {
    if (currentUser.role === "admin") {
      return { projects, tasks, users };
    }

    if (currentUser.role === "supervisor") {
      const filteredProjects = projects.filter(
        (p) => p.supervisorId === currentUser.id,
      );
      const filteredTasks = tasks.filter((t) =>
        filteredProjects.some((p) => p.id === t.projectId),
      );
      return {
        projects: filteredProjects,
        tasks: filteredTasks,
        users: users.filter((u) => u.role === "fabricator"),
      };
    }

    // fabricator
    const filteredTasks = tasks.filter((t) => t.assignedTo === currentUser.id);
    const filteredProjects = projects.filter((p) =>
      p.fabricatorIds.includes(currentUser.id),
    );
    return {
      projects: filteredProjects,
      tasks: filteredTasks,
      users: [],
    };
  };

  const {
    projects: filteredProjects,
    tasks: filteredTasks,
    users: filteredUsers,
  } = getFilteredData();

  const chartData = filteredProjects
    .map((p) => ({
      name: p.name,
      value: Number(p.revenue),
      status: p.status,
    }))
    .filter((item) => item.value > 0);

  const activeProjects = filteredProjects.filter(
    (p) => p.status === "in-progress",
  ).length;
  const completedTasks = filteredTasks.filter(
    (t) => t.status === "completed",
  ).length;

  const getRevenueData = () => {
    if (currentUser.role === "admin") {
      const totalBudget = filteredProjects.reduce(
        (sum, p) => sum + p.budget,
        0,
      );
      const totalRevenue = filteredProjects.reduce(
        (sum, p) => sum + p.revenue,
        0,
      );
      return {
        title: "Total Revenue",
        value: `${peso}${formatCompactAmount(totalRevenue)}`,
        description: `${peso}${formatCompactAmount(totalBudget)} budgeted`,
        canView: true,
      };
    }

    if (currentUser.role === "supervisor") {
      const totalBudget = filteredProjects.reduce(
        (sum, p) => sum + p.budget,
        0,
      );
      const totalSpent = filteredProjects.reduce((sum, p) => sum + p.spent, 0);
      return {
        title: "Project Budget",
        value: `${peso}${formatCompactAmount(totalBudget)}`,
        description: `${peso}${formatCompactAmount(totalSpent)} spent`,
        canView: true,
      };
    }

    // fabricator
    const totalProjectValue = filteredProjects.reduce(
      (sum, p) => sum + p.revenue,
      0,
    );
    return {
      title: "Assigned Value",
      value: `${peso}${formatCompactAmount(totalProjectValue)}`,
      description: `${filteredProjects.length} assigned projects`,
      canView: true,
    };
  };

  const revenueData = getRevenueData();

  const stats = [
    {
      title: "Active Projects",
      value: activeProjects.toString(),
      icon: FolderOpen,
      description: `${filteredProjects.length} total projects`,
      onClick: () => navigateToView("projects"),
    },
    {
      title: "Completed Tasks",
      value: completedTasks.toString(),
      icon: CheckSquare,
      description: `${filteredTasks.length} total tasks`,
      onClick: () => navigateToView("tasks"),
    },
    ...(currentUser.role !== "fabricator"
      ? [
          {
            title: "Team Members",
            value: filteredUsers.length.toString(),
            icon: Users,
            description: "Available resources",
            onClick: () => navigateToView("users"),
          },
        ]
      : []),
    {
      title: revenueData.title,
      value: revenueData.value,
      icon: PhilippinePeso,
      description: revenueData.description,
      onClick: () => {
        if (currentUser.role === "fabricator") {
          setShowRevenueDetails(true);
        } else {
          navigateToView("revenue");
        }
      },
    },
  ];

  return (
    <>
      <div className="grid gap-3 sm:gap-5 lg:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-5 sm:mb-8">
        {stats.map((stat) => (
          <Card
            key={stat.title}
            elevated
            className="cursor-pointer overflow-hidden p-0 animate-fade-in-up hover-lift"
            onClick={stat.onClick}
          >
            <CardHeader className="flex flex-row items-start justify-between gap-3 sm:gap-4 space-y-0 p-4 sm:p-5 pb-3 sm:pb-4">
              <div>
                <CardTitle className="text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <div className="mt-1.5 sm:mt-2 text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-foreground font-mono">
                  {stat.value}
                </div>
              </div>
              <div className="flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-lg bg-primary/10 text-primary border border-primary/20 shrink-0">
                <stat.icon className="h-4.5 w-4.5 sm:h-5 sm:w-5" />
              </div>
            </CardHeader>
            <CardContent className="border-t border-border/40 px-4 sm:px-5 py-2.5 sm:py-3 bg-muted/20">
              <p className="text-[11px] sm:text-xs text-muted-foreground font-medium truncate">
                {stat.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {showRevenueDetails &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in">
            <div className="w-full max-w-2xl glass-panel-dialog rounded-xl overflow-hidden animate-in zoom-in-95 flex flex-col max-h-[90vh]">
              <div className="flex items-center justify-between p-5 border-b border-border/60 shrink-0 bg-muted/20">
                <h3 className="font-semibold text-lg tracking-tight flex items-center gap-2 text-foreground">
                  <PhilippinePeso className="w-5 h-5 text-primary" />
                  Assigned Projects Distribution
                </h3>
                <button
                  onClick={() => setShowRevenueDetails(false)}
                  className="p-1.5 hover:bg-accent rounded-lg text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="overflow-y-auto p-6 space-y-6">
                {filteredProjects.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8 text-sm">
                    No assigned projects found.
                  </p>
                ) : (
                  <>
                    <div className="w-full h-[280px] flex justify-center items-center">
                      <ResponsiveContainer width="99%" height="100%">
                        <PieChart>
                          <Pie
                            data={chartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={85}
                            paddingAngle={4}
                            dataKey="value"
                            nameKey="name"
                          >
                            {chartData.map((_entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value: number) =>
                              `${peso}${value.toLocaleString()}`
                            }
                            contentStyle={{
                              backgroundColor: "var(--card)",
                              borderRadius: "8px",
                              border: "1px solid var(--border)",
                              color: "var(--foreground)",
                              fontSize: "12px",
                            }}
                          />
                          <Legend verticalAlign="bottom" height={36} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="space-y-3">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Project Breakdown
                      </h4>
                      {filteredProjects.map((project, index) => (
                        <div
                          key={project.id}
                          className="flex items-center justify-between p-3.5 border border-border/60 rounded-lg hover:bg-accent/40 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-xs shrink-0"
                              style={{
                                backgroundColor: COLORS[index % COLORS.length],
                              }}
                            />
                            <div className="space-y-1">
                              <p className="font-medium text-sm text-foreground">
                                {project.name}
                              </p>
                              <span className="text-[10px] px-2 py-0.5 rounded-sm capitalize bg-muted text-muted-foreground border border-border/40 font-mono">
                                {project.status.replace("-", " ")}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-sm text-foreground font-mono">
                              {peso}
                              {project.revenue.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="p-4 border-t border-border/60 bg-muted/20 flex justify-between items-center shrink-0">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Total Value
                </span>
                <span className="text-base font-bold text-foreground font-mono">
                  {revenueData.value}
                </span>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <AnnouncementBoard currentUser={currentUser} />
    </>
  );
}
